/**
 * LandingHero — animated cash-flow hero replacing the previous static snapshot.
 *
 * Anatomy
 * -------
 *   • Rotating pain-point phrases ("¿Llego al alquiler?" → "¿Puedo invitar
 *     a cenar?" → "¿Acepto ese proyecto?") above the headline. Phrases
 *     swap every 3 seconds with a clean fade — no jank, no layout shift.
 *   • A mock "cash this week" widget on the right that:
 *       1) counts a number up from 0 to a localized target,
 *       2) fills a circular risk meter from 0 → mid (amber),
 *       3) shows a tiny safe-to-use chip that pulses once when ready.
 *   • All animations are pure CSS + a single requestAnimationFrame
 *     loop. No external lib, no Framer Motion. Lighthouse-friendly.
 *
 * The component is intentionally decorative — it is NOT bound to real
 * data. The numbers are static targets chosen to feel believable in
 * the active country's currency.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { TrendingDown, ShieldCheck, Sparkles, AlertTriangle } from "lucide-react";

const COUNTRY_DEFAULTS = {
  CO: { currency: "COP", locale: "es-CO", week: 480_000, month_in: 5_800_000, month_out: 4_100_000 },
  MX: { currency: "MXN", locale: "es-MX", week: 4_200,    month_in: 48_000,    month_out: 34_000 },
  AR: { currency: "ARS", locale: "es-AR", week: 165_000,  month_in: 1_950_000, month_out: 1_400_000 },
  BR: { currency: "BRL", locale: "pt-BR", week: 950,      month_in: 11_500,    month_out: 8_200 },
  CL: { currency: "CLP", locale: "es-CL", week: 190_000,  month_in: 2_300_000, month_out: 1_600_000 },
  PE: { currency: "PEN", locale: "es-PE", week: 840,      month_in: 10_200,    month_out: 7_300 },
  UY: { currency: "UYU", locale: "es-UY", week: 9_400,    month_in: 115_000,   month_out: 82_000 },
};

// Rotating pain points — one set per supported language. Kept short so
// they fit on a single line on mobile.
const PAIN_PHRASES = {
  es: [
    "¿Llego al alquiler este mes?",
    "¿Puedo invitar a cenar?",
    "¿Acepto ese proyecto nuevo?",
    "¿Me alcanza para el seguro?",
    "¿Me animo a ese viaje?",
  ],
  en: [
    "Can I make rent this month?",
    "Can I afford that dinner?",
    "Should I take that gig?",
    "Will I cover insurance?",
    "Can I book that trip?",
  ],
  pt: [
    "Vou conseguir pagar o aluguel?",
    "Posso convidar para jantar?",
    "Aceito esse novo projeto?",
    "Sobra para o seguro?",
    "Posso viajar este mês?",
  ],
};

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

export function LandingHeroAnimated({ country = "CO", lang = "es" }) {
  const cfg = COUNTRY_DEFAULTS[country] || COUNTRY_DEFAULTS.CO;
  const phrases = PAIN_PHRASES[lang] || PAIN_PHRASES.es;
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [animValue, setAnimValue] = useState(0);
  const [riskFill, setRiskFill] = useState(0); // 0 → 0.62
  const animRef = useRef(null);

  // Rotate the pain phrase every 3 seconds.
  useEffect(() => {
    const t = setInterval(() => setPhraseIdx((i) => (i + 1) % phrases.length), 3500);
    return () => clearInterval(t);
  }, [phrases.length]);

  // Count-up the cash number once on mount.
  useEffect(() => {
    const target = cfg.week;
    const duration = 1500;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setAnimValue(Math.floor(target * easeOutCubic(t)));
      setRiskFill(0.62 * easeOutCubic(t));
      if (t < 1) animRef.current = requestAnimationFrame(tick);
    };
    animRef.current = requestAnimationFrame(tick);
    return () => animRef.current && cancelAnimationFrame(animRef.current);
  }, [cfg.week]);

  // Localized formatting that drops fractional cents for large
  // currencies (COP / CLP / ARS / PYG) but keeps them for small ones.
  const formatted = useMemo(() => {
    try {
      const maxDigits = cfg.week >= 1000 ? 0 : 2;
      return new Intl.NumberFormat(cfg.locale, {
        style: "currency",
        currency: cfg.currency,
        maximumFractionDigits: maxDigits,
      }).format(animValue);
    } catch {
      return `${cfg.currency} ${animValue.toLocaleString()}`;
    }
  }, [animValue, cfg]);

  // Risk meter is rendered as an SVG ring. The full circumference is
  // 2 * pi * r; the stroke-dasharray pair animates from full → fill.
  const ringR = 56;
  const ringC = 2 * Math.PI * ringR;
  const ringDash = `${ringC * riskFill} ${ringC * (1 - riskFill)}`;

  return (
    <div className="relative" data-testid="landing-hero-animated">
      {/* Rotating pain phrase */}
      <div className="h-8 lg:h-9 mb-3 overflow-hidden relative" data-testid="landing-pain-rotator">
        <div
          key={phraseIdx}
          className="absolute inset-0 lf-fade-in-up text-sm lg:text-base text-amber-700 font-semibold tracking-tight italic"
        >
          “{phrases[phraseIdx]}”
        </div>
      </div>

      {/* Card */}
      <div className="relative bg-white rounded-3xl border border-cream-300 p-6 lg:p-8 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.25)] overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/40 via-transparent to-amber-50/30 pointer-events-none" />

        <div className="relative grid grid-cols-2 gap-4 lg:gap-6">
          {/* Left: numeric */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
              {lang === "en" ? "This week" : lang === "pt" ? "Esta semana" : "Esta semana"}
            </p>
            <p className="mt-1 text-[10px] text-slate-500">
              {lang === "en" ? "Safe to use" : lang === "pt" ? "Pode usar" : "Puedes usar"}
            </p>
            <div className="mt-2 font-mono-num text-2xl lg:text-3xl xl:text-4xl font-bold text-emerald-700 tabular-nums leading-tight break-words" data-testid="landing-hero-amount">
              {formatted}
            </div>
            <div className="mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[10px] font-semibold text-emerald-700">
              <ShieldCheck className="h-3 w-3" />
              {lang === "en" ? "Safe" : lang === "pt" ? "Seguro" : "Seguro"}
            </div>
          </div>

          {/* Right: risk ring */}
          <div className="flex flex-col items-center justify-center">
            <svg viewBox="0 0 140 140" className="h-28 w-28 lg:h-32 lg:w-32 -rotate-90" data-testid="landing-hero-risk-ring">
              <circle cx="70" cy="70" r={ringR} fill="none" stroke="#F1F5F9" strokeWidth="10" />
              <circle
                cx="70"
                cy="70"
                r={ringR}
                fill="none"
                stroke="url(#riskGrad)"
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={ringDash}
                style={{ transition: "stroke-dasharray 0.6s ease-out" }}
              />
              <defs>
                <linearGradient id="riskGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#10B981" />
                  <stop offset="0.6" stopColor="#F59E0B" />
                  <stop offset="1" stopColor="#EF4444" />
                </linearGradient>
              </defs>
            </svg>
            <div className="mt-1 text-center">
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-600">
                {lang === "en" ? "Month risk" : lang === "pt" ? "Risco mês" : "Riesgo mes"}
              </p>
              <p className="font-display text-lg font-bold text-amber-700">
                {lang === "en" ? "Mid" : lang === "pt" ? "Médio" : "Medio"}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom: small movement stub */}
        <div className="relative mt-5 pt-4 border-t border-slate-100 flex items-center gap-2 text-[11px] text-slate-500">
          <Sparkles className="h-3 w-3 text-emerald-500" />
          <span className="flex-1 truncate">
            {lang === "en" ? "Logged via chat · 1 min ago" : lang === "pt" ? "Registrado por chat · 1 min" : "Registrado por chat · 1 min"}
          </span>
          <span className="font-mono-num text-emerald-700 font-semibold">
            +{new Intl.NumberFormat(cfg.locale, { style: "currency", currency: cfg.currency, maximumFractionDigits: 0 }).format(Math.round(cfg.month_in * 0.18))}
          </span>
        </div>
      </div>
    </div>
  );
}
